import { getClientName } from '@core/util/eregistration';
import { MortgageTag } from '@core/util/tags';
import { BusinessClient, PersonClient } from '@models/clients';
import { IdentificationPriority } from '@models/identification';
import { MatterDocumentType, MatterFile } from '@models/matter-file';
import { Selector } from '@ngxs/store';
import { ClientsState } from '@state/clients';
import { MatterMortgagesState } from '@state/matter/matter_mortgages';
import { MatterUploadedFilesSelectors } from '@state/matter/uploaded-files/matter-uploaded-files.selectors';

export interface VirtualizedMatterFile extends MatterFile {
	virtualized?: boolean;
}

export class MergedUploadedMatterDocumentSelectors {
	@Selector([
		MatterUploadedFilesSelectors.getAllUploadedFiles,
		ClientsState.getAllClients,
	])
	static getUploadedDocs(
		items: MatterFile[],
		clients: (PersonClient | BusinessClient)[],
	): VirtualizedMatterFile[] {
		const virtualizedItems = [];

		for (const element of clients) {
			virtualizedItems.push(
				...MergedUploadedMatterDocumentSelectors.GetClientUploadedItems(
					element,
				),
			);
		}

		return [...items, ...virtualizedItems];
	}

	static GetClientUploadedItems(
		client: PersonClient | BusinessClient,
	): VirtualizedMatterFile[] {
		const uploadedIds = [];
		const uploadedIdPrimary = this.BuildVirtualizeIdFile(
			client as PersonClient,
			IdentificationPriority.PRIMARY,
		);
		const uploadedIdSecondary = this.BuildVirtualizeIdFile(
			client as PersonClient,
			IdentificationPriority.SECONDARY,
		);

		if (uploadedIdPrimary) {
			uploadedIds.push(uploadedIdPrimary);
		}
		if (uploadedIdSecondary) {
			uploadedIds.push(uploadedIdSecondary);
		}

		return uploadedIds;
	}

	static BuildVirtualizeIdFile(
		element: PersonClient,
		priority: IdentificationPriority,
	): VirtualizedMatterFile {
		if ((element as PersonClient)?.identification?.[priority]) {
			const id = (element as PersonClient)?.identification?.[priority];
			if (!id?.latestIdUrl) {
				return null;
			}

			if (id.latestIdUrl) {
				return {
					id: `${element.id}-id-${priority}`,
					name: `${getClientName(element)} ${priority} ID`,
					type: MatterDocumentType.IDENTIFICATION,
					uploadedAt: new Date(id.dateOfVerification).getTime(),
					virtualized: true,
					url: id.latestIdUrl,
					fileType: null,
					systemTags: [],
					keywords: [],
				};
			}
		}
	}

	@Selector([
		MergedUploadedMatterDocumentSelectors.getUploadedDocs,
		MatterMortgagesState.getSelectedMortgageId,
	])
	static getUploadedMortgageDocs(
		uploadedDocs: VirtualizedMatterFile[],
		selectedMortgageId: string,
	): VirtualizedMatterFile[] {
		return uploadedDocs.filter((doc) =>
			doc?.systemTags?.includes(MortgageTag(selectedMortgageId)),
		);
	}
}
